/**
 * CockpitLayout — the Creative OS Home cockpit shell, now a real React Router
 * layout route (NavRail + optional page chat + <Outlet/>) instead of a
 * single component owning a client-side nav switch. Each nav destination is a
 * real, deep-linkable child route; this file only owns cross-view shell state
 * (theme, nav open state) and hands it to children via Outlet context.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router";
import "./cockpit.css";
import { CoShellPortalContext } from "./CoShellPortalContext";
import NavRail from "./components/NavRail";
import NotificationIsland from "./components/NotificationIsland";
import FeedbackModal from "./components/FeedbackModal";
import WrongAccountModal from "./components/WrongAccountModal";
import VantageLineView from "./views/VantageLineView";
import HomePage from "./home";
import { useHomeData } from "./home/useHomeData";
import HomeComposer from "./home/HomeComposer";
import UniverseView from "./views/UniverseView";
import SignalsSurface from "@/app/routes/dashboard/market-signals/SignalsSurface";
import { useSignals } from "./data/useCreativeOsHome";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useWorkspaceDeepLink } from "@/hooks/useWorkspaceDeepLink";
import {
  useMyInvites,
} from "@/app/routes/dashboard/settings/api/useOrganization";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import BrandAgentRail from "./agent/BrandAgentRail";
import AgentRailDisabled, { type AgentRailOffReason } from "./agent/AgentRailDisabled";
import { useAgentFocus, useAgentFocusStore } from "./agent/agentFocusStore";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { isDevMode } from "@/config/envvars";
import { useAppearanceStore, useResolvedTheme } from "@/stores/appearanceStore";
import NoWorkspaceScreen from "./components/settings/NoWorkspaceScreen";
import { usePermissions } from "@/hooks/usePermissions";
import { GUEST_HOME_PATH, isGuestBlockedPath } from "./guestAccess";
import { useGetBrands } from "@/hooks/useGetBrands";
import { FirstBrandScreen, WelcomeLine, shouldWelcome } from "@/components/gettingIn/FirstRun";
import NoBrandScreen from "./components/settings/NoBrandScreen";

/** Below this the nav rail cannot afford permanent floor space and becomes a drawer. */
const COMPACT_SHELL = "(max-width: 1023px)";

const MENU_ICON = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

export type ChatPanelOptions = {
  /** Main column uses internal scroll regions (universe fold) instead of page scroll. */
  contained?: boolean;
};

export type CockpitContext = {
  theme: "light" | "dark";
  dark: boolean;
  setChatPanel: (panel: ReactNode | null, options?: ChatPanelOptions) => void;
  /** Open the navigation drawer from a view's own menu button (the phone
   *  Trends feed draws its own top bar). Focus returns to `returnFocusTo` when
   *  the drawer closes, or to the shell's trigger when none is given. */
  openNav: (returnFocusTo?: HTMLElement | null) => void;
};

const RAIL_OPEN_KEY = "co-rail-open";

export function useCockpitContext() {
  return useOutletContext<CockpitContext>();
}

export default function CockpitLayout() {
  const location = useLocation();
  const { logout, user, selectedOrganization } = useCurrentUser();
  const { isGuest } = usePermissions();
  // ?orgId=&brandId= from an emailed link: switch workspace before the guest
  // gate decides, and say so when the link was meant for another account.
  const deepLink = useWorkspaceDeepLink();
  const brandsQuery = useGetBrands();
  const navigate = useNavigate();
  const signInAsLinkOwner = useCallback(() => {
    const target = deepLink.mismatch?.target;
    deepLink.dismissMismatch();
    logout();
    if (target) navigate(`/login?redirect=${encodeURIComponent(target)}`, { replace: true });
  }, [deepLink, logout, navigate]);
  // Signals are hidden from guests; their rail never counts pending ones.
  const { pendingSignals } = useSignals({ enabled: !isGuest });
  const { data: myInvites } = useMyInvites();
  const invitesPending = myInvites?.length ?? 0;
  const [chatPanel, setChatPanelState] = useState<ReactNode | null>(null);
  const [chatPanelOptions, setChatPanelOptions] = useState<ChatPanelOptions>({});
  const setChatPanel = useCallback((panel: ReactNode | null, options?: ChatPanelOptions) => {
    setChatPanelState(panel);
    setChatPanelOptions(options ?? {});
  }, []);

  const selectedBrandId = useWorkspaceStore((st) => st.selectedBrandId);
  // The agent rail is the shell's, not a page's: pages come and go as the user
  // navigates and the conversation must not go with them. Pages publish where
  // they are through the focus store instead of handing this a panel.
  const agentContained = useAgentFocusStore((st) => st.contained);
  // Settings → Appearance owns the theme (per device); the rail toggle picks
  // an explicit light or dark.
  const theme = useResolvedTheme();
  const setThemePreference = useAppearanceStore((st) => st.setTheme);
  const reduceMotion = useAppearanceStore((st) => st.reduceMotion);
  const solidSurfaces = useAppearanceStore((st) => st.solidSurfaces);
  // The icon-and-label rail is the default working state. People can expand it
  // when they want the full group hierarchy, without surrendering page width
  // on every route first.
  /* M6: the rail remembers its last width, per device. */
  const [navOpen, setNavOpenState] = useState(() => {
    try {
      return window.localStorage.getItem(RAIL_OPEN_KEY) === "1";
    } catch {
      return false;
    }
  });
  const setNavOpen = useCallback((next: boolean | ((open: boolean) => boolean)) => {
    setNavOpenState((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      try {
        window.localStorage.setItem(RAIL_OPEN_KEY, value ? "1" : "0");
      } catch {
        /* storage off: the rail just forgets */
      }
      return value;
    });
  }, []);
  const isCompact = useMediaQuery(COMPACT_SHELL);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navTriggerRef = useRef<HTMLButtonElement>(null);
  /* A view's own menu button, when that is what opened the drawer. */
  const navReturnRef = useRef<HTMLElement | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [portalContainer, setPortalContainer] = useState<Element | null>(null);
  const setShellRef = useCallback((node: HTMLDivElement | null) => {
    setPortalContainer(node);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.coTheme = theme;
    return () => {
      delete document.documentElement.dataset.coTheme;
    };
  }, [theme]);

  /* Settings M19, the theme changes: the cross-fade runs where the theme is
     set (appearanceStore.ts setTheme), as one view transition. */

  useEffect(() => {
    const root = document.documentElement;
    if (reduceMotion) root.dataset.coMotion = "reduce";
    else delete root.dataset.coMotion;
    if (solidSurfaces) root.dataset.coSurfaces = "solid";
    else delete root.dataset.coSurfaces;
  }, [reduceMotion, solidSurfaces]);

  useEffect(() => {
    if (isCompact) return;
    const closeFrame = window.requestAnimationFrame(() => setDrawerOpen(false));
    return () => window.cancelAnimationFrame(closeFrame);
  }, [isCompact]);

  useEffect(() => {
    const closeFrame = window.requestAnimationFrame(() => setDrawerOpen(false));
    return () => window.cancelAnimationFrame(closeFrame);
  }, [location.pathname]);

  // A new page starts at its top, so the corner buttons drop their backing.
  useEffect(() => {
    portalContainer?.removeAttribute("data-page-scrolled");
  }, [location.pathname, portalContainer]);

  useEffect(() => {
    if (!drawerOpen || !isCompact) return;
    const drawer = document.getElementById("co-nav-rail");
    if (!drawer) return;
    const navTrigger = navReturnRef.current ?? navTriggerRef.current;
    const focusFrame = window.requestAnimationFrame(() => {
      const firstDestination = drawer.querySelector<HTMLElement>('nav a[href]');
      const firstControl = drawer.querySelector<HTMLElement>('button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      (firstDestination ?? firstControl)?.focus();
    });
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setDrawerOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", onKeyDown);
      navReturnRef.current = null;
      navTrigger?.focus();
    };
  }, [drawerOpen, isCompact]);

  const hasOrg = (user?.userOrganizations?.length ?? 0) > 0;

  // No-org users stay on a locked screen (message + accept/decline invites only).
  if (user && !hasOrg) {
    return <NoWorkspaceScreen onLogout={logout} />;
  }

  // Figma 10 · First run 01: an Owner with no brand yet starts with one, in
  // place of an empty Home. Settings stays open ("Invite your team first").
  if (
    selectedOrganization?.role === "Owner" &&
    brandsQuery.data?.length === 0 &&
    !location.pathname.startsWith("/dashboard/settings")
  ) {
    return <FirstBrandScreen firstName={(user?.name ?? "").trim().split(/\s+/)[0] ?? ""} onSignOut={logout} />;
  }

  // Hold the page for the one render it takes to apply a linked workspace.
  if (deepLink.pending) return null;

  // Everyone else who cannot create a brand (Contributor, Viewer, client) and
  // finds none: say who can fix it. A client's list is the brands shared with
  // them, so for a client this is "nothing shared yet". Runs before the guest
  // redirect, which would otherwise land them in a Workshop with no brand.
  if (
    selectedOrganization &&
    selectedOrganization.role !== "Owner" &&
    brandsQuery.data?.length === 0 &&
    !location.pathname.startsWith("/dashboard/settings")
  ) {
    return (
      <NoBrandScreen
        membership={{ ...selectedOrganization, role: selectedOrganization.role }}
        otherWorkspaces={(user?.userOrganizations ?? []).filter(
          (uo) => uo.organization.id !== selectedOrganization.organization.id,
        )}
        checking={brandsQuery.isFetching}
        onCheckAgain={() => void brandsQuery.refetch()}
        onLogout={logout}
      />
    );
  }

  // A guest who follows an old link (or lands on Home) goes to the Workshop.
  if (isGuest && isGuestBlockedPath(location.pathname)) {
    return <Navigate to={GUEST_HOME_PATH} replace />;
  }

  const membership = selectedOrganization;
  const welcome =
    user && membership && membership.role !== "Owner" && membership.role !== "Guest" &&
    location.pathname === "/dashboard" &&
    shouldWelcome(user.id, membership.organization.id, membership.createdAt)
      ? {
          userId: user.id,
          organizationId: membership.organization.id,
          title: `Welcome to ${membership.organization.name}${user.name ? `, ${user.name.trim().split(/\s+/)[0]}` : ""}`,
        }
      : null;

  const outletContext: CockpitContext = {
    theme,
    dark: theme === "dark",
    setChatPanel,
    openNav: (returnFocusTo) => {
      navReturnRef.current = returnFocusTo ?? null;
      if (isCompact) setDrawerOpen(true);
      else setNavOpen(true);
    },
  };

  const isWorkshopMode = location.pathname.includes("/workshop");
  // The brand agent reads strategy, signals and calendar internals: team only.
  // Where it cannot be used the dock stays, disabled, so no page ever gains or
  // loses the strip's width.
  const agentRailOff: AgentRailOffReason | null = isWorkshopMode
    ? "workshop"
    : isGuest
      ? "client"
      : !selectedBrandId
        ? "no-brand"
        : null;
  const chatContained = isWorkshopMode
    ? chatPanelOptions.contained === true
    : agentContained;
  /* NOTE: isGalleryMode used to gate full-field rendering by pathname, an
     opt-in allowlist that had grown to five entries — trends, market-signals,
     references, brand-media and strategy — each with a comment explaining the
     bug that forced it in. Strategy's said it plainly: it was added "to stop
     its width depending on whether the agent rail happens to be mounted".

     Opt-in was the wrong default. A new view had to remember to enrol, a route
     rename broke it silently, and the same page rendered at two different
     widths depending on whether a chat panel had registered yet. Full field is
     now unconditional and the list is gone. */
  const railOpen = isCompact ? drawerOpen : navOpen;
  const toggleNav = () => (isCompact ? setDrawerOpen((v) => !v) : setNavOpen((v) => !v));
  // The page (co-grid-field) owns the single scrollbar for every view except
  // "contained" ones (e.g. Universe / Workshop), which manage their own
  // internal scroll regions and must not also scroll at the page level.
  const pageScrolls = isWorkshopMode ? false : !chatContained;

  const mainPadding = (() => {
    if (isWorkshopMode) {
      if (!pageScrolls) return "0";
      return isCompact ? "16px 12px 24px" : "24px 24px 32px";
    }
    const bottom = pageScrolls ? (isCompact ? "28px" : "36px") : "0";
    // The compact shell keeps a small top lane for the navigation trigger.
    // Without it, full-width page titles compete with the only route out.
    if (isCompact) return `56px 12px ${bottom}`;
    /* One padding for every view, open or closed. The dock has its own edge and
       sits outside this column, so the gutter never has to change to make room
       for it. 26 on the left, the library View frame's gutter (style B). */
    return `30px 32px ${bottom} 26px`;
  })();
  /* The same gutters as properties, so a full-bleed band (the wall's header and
     filter bar) can reach the canvas edges and pad itself back. Desktop only:
     the compact shell keeps its bands inside the padding. */
  const bleed = !isWorkshopMode && !isCompact
    ? ({
        "--co-bleed-start": "26px",
        "--co-bleed-end": "32px",
        "--co-bleed-top": "30px",
        "--co-bleed-bottom": pageScrolls ? "36px" : "0px",
      } as React.CSSProperties)
    : undefined;

  return (
    <CoShellPortalContext.Provider value={portalContainer}>
      <div
        ref={setShellRef}
        className={`co-shell${theme === "dark" ? " dark" : ""}`}
      >
      {isCompact ? (
        <div
          className="co-nav-scrim"
          aria-hidden="true"
          onClick={() => setDrawerOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: "var(--z-index-drawer)",
            background: "var(--co-scrim)",
            opacity: drawerOpen ? 1 : 0,
            pointerEvents: drawerOpen ? "auto" : "none",
            // M3: the scrim comes in over 180ms ease-out and leaves in 160ms.
            transition: drawerOpen ? "opacity 180ms cubic-bezier(0, 0, 0.2, 1)" : "opacity 160ms cubic-bezier(0.4, 0, 1, 1)",
          }}
        />
      ) : null}
      <NavRail
        open={railOpen}
        overlay={isCompact}
        onNavigate={() => setDrawerOpen(false)}
        signalsPending={pendingSignals.length}
        invitesPending={invitesPending}
        theme={theme}
        /* The rail folds itself; in the drawer the same button closes it. */
        onToggle={toggleNav}
        onToggleTheme={() => setThemePreference(theme === "dark" ? "light" : "dark")}
        onOpenFeedback={() => setFeedbackOpen(true)}
        hideBrandPicker={!hasOrg}
      />
      <FeedbackModal isOpen={feedbackOpen} onOpenChange={setFeedbackOpen} />
      {/* Figma 10 · First run 02 (M05): a teammate's first landing. Clients
          land on the Workshop and get no line until their Home exists (D10). */}
      {welcome ? (
        <WelcomeLine
          userId={welcome.userId}
          organizationId={welcome.organizationId}
          title={welcome.title}
          body="Every brand the team makes is here. Start with this week."
        />
      ) : null}
      <WrongAccountModal
        mismatch={deepLink.mismatch}
        onDismiss={deepLink.dismissMismatch}
        onSignOut={signInAsLinkOwner}
      />

      <div
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            // position: relative anchors the island over the content field,
            // above whatever the view is doing.
            position: "relative",
            /* NO HOLE WHERE THE HEADER WAS.
               I first kept 80px here to hold the view-header contract's 110/215,
               which was the wrong instinct: those numbers described a
               composition that INCLUDED a header band, and preserving them
               without it just reproduced the band as empty space — which is
               exactly what Emad saw. The contract re-bases on a shell that has
               no header, and the page's own padding is the air. */
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: isWorkshopMode ? "row" : "column",
            overflow: "hidden",
          }}
        >
          {isCompact ? (
            <button
              ref={navTriggerRef}
              type="button"
              className="co-icon-ghost ws-platform-nav-trigger"
              onClick={toggleNav}
              aria-label={drawerOpen ? "Close navigation" : "Open navigation"}
              aria-expanded={drawerOpen}
              aria-controls="co-nav-rail"
            >
              {MENU_ICON}
            </button>
          ) : null}
          <NotificationIsland />
          <div
            className="co-grid-field"
            /* The phone's corner buttons take a glass backing once the page
               scrolls under them. Captured, so pages that scroll inside their
               own panes (Universe) count too; sideways strips are ignored. An
               attribute, not state: scrolling must not re-render the shell. */
            onScrollCapture={(e) => {
              const scroller = e.target;
              // Portalled popovers reach here through React, not the page.
              if (!(scroller instanceof HTMLElement) || !e.currentTarget.contains(scroller)) return;
              if (scroller.scrollHeight <= scroller.clientHeight) return;
              portalContainer?.toggleAttribute("data-page-scrolled", scroller.scrollTop > 8);
            }}
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflowY: pageScrolls ? "auto" : "hidden",
              overflowX: "hidden",
              /* The field keeps a scrollbar's gutter so a scrolling page never
                 jumps sideways (cockpit.css). A view that never scrolls the
                 field (Workshop, Universe) would only lose that strip on its
                 right edge, so it takes the whole width. */
              scrollbarGutter: pageScrolls ? undefined : "auto",
              display: isWorkshopMode ? "flex" : undefined,
              flexDirection: isWorkshopMode ? "column" : undefined,
            }}
          >
            <main
              style={{
                /* Chat is a shell sibling of this column — right padding is the
                   only gap between the view and the agent rail. Keep it tight. */
                padding: mainPadding,
                ...bleed,
                /* Every view takes the whole desk. There is no shell-level
                   reading cap and no centred column: a page that starts inside
                   a centred 1440 drifts its edges away from the gutter every
                   other view shares, so views stopped lining up with each other
                   at width. Measure is per BLOCK, not per page — prose caps
                   itself at --width-prose-max where it needs to. */
                maxWidth: "none",
                margin: 0,
                height: pageScrolls ? undefined : "100%",
                display: pageScrolls ? undefined : "flex",
                flexDirection: pageScrolls ? undefined : "column",
                minHeight: pageScrolls ? undefined : 0,
              }}
            >
              <div
                key={location.pathname}
                className="co-page-in"
                style={
                  !pageScrolls
                    ? {
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                      }
                    : undefined
                }
              >
                <Outlet context={outletContext} />
              </div>
            </main>
          </div>

          {isWorkshopMode ? chatPanel : null}
        </div>
      </div>

      {/* A shell sibling so the desk actually makes room for it. Creo off for
          this page renders the same strip, disabled, except in the Workshop,
          which has its own chat and no rail at all. */}
      {isWorkshopMode ? null : selectedBrandId && !agentRailOff ? (
        <BrandAgentRail key={selectedBrandId} brandId={selectedBrandId} />
      ) : (
        <AgentRailDisabled reason={agentRailOff ?? "no-brand"} />
      )}
      </div>
    </CoShellPortalContext.Provider>
  );
}

/**
 * Home — the index child route (Figma 11). It reads everything through one hook
 * and takes it as a single prop, so /design/home-preview renders the same page
 * against fixtures. The composer is a control and brings its own hooks.
 *
 * VantageLineView is still reachable at /dashboard?view=vantage.
 */
export function HomeRoute() {
  const navigate = useNavigate();
  useAgentFocus({ surface: "home" });
  const [params] = useSearchParams();
  // Dev only: ?since=<ms> pins "since your last visit" for the harness.
  const sinceParam = isDevMode ? Number(params.get("since")) : NaN;
  const { data, state } = useHomeData({ sinceOverride: Number.isFinite(sinceParam) && sinceParam > 0 ? sinceParam : null });
  const brandId = useWorkspaceStore((st) => st.selectedBrandId);

  if (params.get("view") === "vantage") {
    return (
      <VantageLineView
        onOpenSignals={() => navigate("market-signals")}
        onOpenUniverse={() => navigate("brand-universe")}
        onOpenStrategy={() => navigate("strategy")}
        onOpenCalendar={() => navigate("content-calendar")}
      />
    );
  }

  return (
    <HomePage
      data={data}
      state={state}
      composer={brandId ? <HomeComposer key={brandId} brandId={brandId} brandName={data.brand.name} /> : null}
    />
  );
}

export function UniverseRoute() {
  const { dark } = useCockpitContext();
  return <UniverseView dark={dark} />;
}

export function SignalsRoute() {
  useAgentFocus({ surface: "signals" });
  const { dark } = useCockpitContext();
  return <SignalsSurface dark={dark} embedded />;
}

/** Placeholder for nav destinations without a real view yet. */
export function Roadmap({ label, wave }: { label: string; wave?: string }) {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: 440, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 14, padding: "60px 20px" }}>
      <div style={{ width: 46, height: 46, border: "1px solid var(--co-line2)", display: "grid", placeItems: "center", color: "var(--co-accent)" }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><circle cx="12" cy="12" r="3" /><ellipse cx="12" cy="12" rx="10" ry="4.4" /></svg>
      </div>
      <div style={{ fontFamily: "var(--font-section)", fontWeight: 800, fontSize: 30, letterSpacing: "-.01em" }}>{label}</div>
      {wave && <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".1em", color: "var(--co-faint)" }}>{wave}</div>}
      <div style={{ fontSize: 14, color: "var(--co-muted)", maxWidth: 360, lineHeight: 1.55 }}>This view is forming. For now, everything that needs you lives on your home read.</div>
      <button className="co-btn" onClick={() => navigate("..")} style={{ marginTop: 6, padding: "11px 20px", fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 12, letterSpacing: ".1em", textTransform: "uppercase", cursor: "pointer" }}>Back to home</button>
    </div>
  );
}
